import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, User } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { monitor, captureException } from '@/lib/monitoring';

/** Firestore user profile (from users/{uid}) */
export type UserProfile = {
  role: 'customer' | 'provider';
  firstName: string;
  lastName: string;
  phone: string;
  gender: string;
  nationality: string;
  dateOfBirth: any;
  nationalId: string;
  createdAt: any;
  updatedAt: any;
  profilePhoto?: string;
};

type AuthContextValue = {
  currentUser: User | null;
  userProfile: UserProfile | null;
  isProvider: boolean;
  loading: boolean;
  /** Email/password login for SignIn page */
  // eslint-disable-next-line no-unused-vars
  login: (email: string, password: string) => Promise<void>;
  /** Sign out current user */
  logout: () => Promise<void>;
  /** Re-fetch the user profile from Firestore */
  refreshProfile: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isProvider, setIsProvider] = useState(false);
  const [loading, setLoading] = useState(true);
  const isDev = import.meta.env.DEV;

  /** Fetch users/{uid} and check providers collection */
  const loadProfile = useCallback(
    async (uid: string) => {
      try {
        // Read users/{uid}
        const userSnap = await getDoc(doc(db, 'users', uid));
        if (userSnap.exists()) {
          setUserProfile(userSnap.data() as UserProfile);
        } else {
          setUserProfile(null);
        }

        // Check providers collection for a doc where userId == uid
        const provQ = query(collection(db, 'providers'), where('userId', '==', uid));
        const provSnap = await getDocs(provQ);
        setIsProvider(!provSnap.empty);
      } catch (err) {
        captureException(err, { area: 'auth.loadProfile' });
        if (isDev) monitor.warn('Failed to load user profile', { uid });
      }
    },
    [isDev],
  );

  const refreshProfile = useCallback(async () => {
    if (currentUser) await loadProfile(currentUser.uid);
  }, [currentUser, loadProfile]);

  useEffect(() => {
    if (isDev) monitor.debug('Setting up auth state listener');
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        if (isDev) monitor.info('Auth state: logged in', { uid: user.uid });
        setCurrentUser(user);
        await loadProfile(user.uid);
      } else {
        if (isDev) monitor.info('Auth state: logged out');
        setCurrentUser(null);
        setUserProfile(null);
        setIsProvider(false);
      }
      setLoading(false);
    });
    return () => unsub();
  }, [isDev, loadProfile]);

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        await signInWithEmailAndPassword(auth, email, password);
        if (isDev) monitor.info('Login successful');
      } catch (err) {
        // err is already unknown in TS catch clause; no need for assertion (removed per Sonar S4325)
        captureException(err, { area: 'auth.login' });
        monitor.error('Login failed');
        throw err;
      }
    },
    [isDev],
  );

  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      if (isDev) monitor.info('Logout successful');
    } catch (err) {
      // err is unknown by default; assertion removed (Sonar S4325)
      captureException(err, { area: 'auth.logout' });
      monitor.error('Logout failed');
      throw err;
    }
  }, [isDev]);

  // Removed effect that only logged state changes to reduce noise and avoid unnecessary side-effects

  const value = useMemo<AuthContextValue>(
    () => ({
      currentUser,
      userProfile,
      isProvider,
      loading,
      login,
      logout,
      refreshProfile,
    }),
    [currentUser, userProfile, isProvider, loading, login, logout, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};
